<template>
  <div>
    <b-modal
      id="import-image-modal"
      :title="isImporting ? 'Importing...' : 'Import Image'"
      size="lg"
      hide-footer
      no-close-on-backdrop
      @show="onModalOpen"
      @hide="onModalHide"
    >
      <b-tabs
        pills
        content-class="mt-3"
      >
        <b-tab
          title="Upload Image"
          @click="importType = IMPORT_TYPE.DROPZONE; currentStep = 1"
        >
          <div
            v-show="currentStep === 1"
            class="upload-image-container"
          >
            <Vue2Dropzone   
              id="ImportImageDropzone"
              ref="fileInput"
              :options="dropzoneOptions"
              @vdropzone-file-added="setFile"
              @vdropzone-removed-file="setFile"
            />
          </div>
        </b-tab>
        <b-tab
          title="Select From Browser"
          @click="importType = IMPORT_TYPE.BROWSER; currentStep = 1"
        >
          <div
            v-show="currentStep === 1"
            class="import-image-browser"
          >
            <FolderContentView
              :is-only-folder="true"
              :can-create-folder="mode === 'selectAndCreate'"
              :page-size="8"
              @onSelectedItems="(items) => selectedItems = items"
            />
          </div>
        </b-tab>
      </b-tabs>
      <div
        v-if="templateSelectAvailable"
        class="mb-3"
      >
        <Multiselect
          v-model="selectedLayout"
          :options="layouts"
          :loading="isLoading"
          searchable
          multiple
          :custom-label="customLabel"
          track-by="id"
          class="mb-3"
          placeholder="Export to layout"
          @search-change="searchCustomerLayouts"
          @select="(option) => selectedLayout = [option]"
        >
          <template
            v-if="layouts.length < layoutsTotalCount"
            #afterList
          >
            <div class="load-more-btn">
              <b-button
                :disabled="isLoading"
                variant="outline-primary"
                @click="getLayouts"
              >
                Load More
              </b-button>
            </div>
          </template>
        </Multiselect>
        <b-form-radio-group
          v-model="selectedStrategy"
          name="select-strategy"
          :options="options"
        />
        <SelectTemplateBanner
          v-if="selectedStrategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY"
          v-model="selectedTemplates"
          class="mt-3"
          :customers="customers"
        />
      </div>
      <div 
        class="d-flex "
        :class="currentStep > 1 ? 'justify-content-between' : 'justify-content-end'"
      >
        <b-button
          v-if="currentStep > 1"
          variant="outline-primary"
          class="mt-3"
          @click="handlePrevStep"
        >
          &larr; Previous Step
        </b-button>
        <b-button
          :disabled="!nextStepAvailable"
          variant="outline-primary"
          class="mt-3"
          @click="handleNextStep"
        >
          {{ currentStep < 2 ? 'Next Step ' : 'Import ' }}&rarr;
        </b-button>
      </div>
      <b-overlay
        :show="isImporting"
        no-wrap
      >
        <template #overlay>
          <div class="text-center d-flex flex-column align-items-center justify-content-center">
            <b-spinner
              variant="primary"
              class="mb-2"
            />
            <p class="mb-0">
              {{ loadingText }}
            </p>
          </div>
        </template>
      </b-overlay>
    </b-modal>
  </div>
</template>
<script>
import Vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Multiselect from 'vue-multiselect';
import layoutAPI from "@frontend/services/api/layout";
import Customer from "@frontend/services/api/customer";
import SelectTemplateBanner from "@frontend/components/common/atoms/SelectTemplateBanner.vue";
import {
  IMPORT_AND_CREATE_STRATEGY,
  IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY
} from "@frontend/group/modules/import-template-from-psd/import-from-psd-new-template";
import { mapGetters } from "vuex";
import FolderContentView from "@frontend/components/modules/file-browser/FolderContentView.vue";

let timer;
const IMPORT_TYPE = {
  BROWSER: 'from_browser',
  DROPZONE: 'from_dropzone'
};
export default {
  name: 'ImportImageModal',
  components: {
    FolderContentView,
    Vue2Dropzone,
    Multiselect,
    SelectTemplateBanner
  },
  data() {
    return {
      importType: IMPORT_TYPE.DROPZONE,
      currentStep: 1,
      isOpen: false,
      file: [],
      dropzoneOptions: {
        url: "http",
        autoProcessQueue: false,
        addRemoveLinks: true,
        createImageThumbnails: true,
        acceptedFiles: "image/*",
        maxFiles: null,
        parallelUploads: 10,
        uploadMultiple: true,
        maxThumbnailFilesize: 50,
        dictDefaultMessage:
          'Paste (CTRL +V), Drag & Drop your files or <a href="#" onclick="event.preventDefault()">Browse</a>',
        dictMaxFilesExceeded: null,
      },
      layoutId: null,
      customers: [],
      selectedTemplates: {
        customer: null,
        templates: [],
      },
      customerId: null,
      layouts: [],
      imagesToImport: [],
      page: 1,
      searchKey: null,
      layoutsTotalCount: null,
      isLoading: false,
      selectedLayout: [],
      isImporting: false,
      options: [
        { text: 'Create new template', value: IMPORT_AND_CREATE_STRATEGY },
        { text: 'Use existing templates', value: IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY },
      ],
      selectedStrategy: IMPORT_AND_CREATE_STRATEGY,
      selectedItems: [],
      mode: 'selectAndCreate', // default, selectAndCreate
      loadingText: 'Importing, please wait...'
    }
  },
  computed: {
    ...mapGetters('layout', ['selectedInstance']),
    IMPORT_TYPE() {
      return IMPORT_TYPE;
    },
    uploadedFilesCount: function() {
      return this.file.length;
    },
    IMPORT_AND_CREATE_STRATEGY() {
      return IMPORT_AND_CREATE_STRATEGY
    },
    IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY() {
      return IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY;
    },
    importAvailable() {
      switch (this.selectedStrategy) {
        case IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY:
          return this.selectedLayout.length && this.selectedTemplates.templates.length
        case IMPORT_AND_CREATE_STRATEGY:
          return this.selectedLayout.length
        default:
          return false
      }
    },
    nextStepAvailable() {
      switch (this.importType) {
        case IMPORT_TYPE.DROPZONE:
          switch (this.currentStep) {
            case 1:
              return this.file.length > 0;
            case 2:
              return this.file.length > 0 && this.importAvailable;
            default:
              return false;

          }
        case IMPORT_TYPE.BROWSER:
          switch (this.currentStep) {
            case 1:
              return this.selectedItems.length > 0 && this.selectedItems.some(item => item.type === 'image');
            case 2:
              return this.selectedItems.length > 0 && this.importAvailable;
            default:
              return false;
          }
        default:
          return false;
      }
    },
    templateSelectAvailable() {
      return this.currentStep === 2;
    }
  },
  mounted() {
    const importBtn = document.querySelector('.import-from-image-btn');
    if (importBtn) {
      importBtn.addEventListener('click', () => {
        this.$bvModal.show('import-image-modal');
      });
    }
    document.addEventListener('paste', this.handlePaste);
  },
  beforeDestroy() {
    document.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    async onModalOpen() {
      await this.loadCustomers()
      this.page = 1;
      this.searchKey = null;
      this.layouts = [];
      this.selectedLayout = [];
      await this.getLayouts();
      // setting current layout if exists
      if (this.selectedInstance) {
        // getting layout id from url
        const result = window.location.href.match(/group\/(\d+)\/show/);
        if (result && result.length > 1) {
          this.layoutId = parseInt(result[1]);
          
          this.selectedLayout = [this.layouts.find((layout) => layout.id === this.layoutId )];  
        } else {
          this.layoutId = null;
        }
      }
      // setting customer that matches selected layout
      if (this.selectedInstance) {
        const customerId = this.selectedInstance?.data.customer_id;
        if (customerId) {
          this.selectedTemplates.customer = this.customers.find(customer => customer.id === customerId) || null;
        }
      } else {
        this.selectedTemplates.customer = null;
      }
    },
    async setFile() {
      this.$nextTick(() => {
        if (!this.$refs.fileInput) return;
        const files = this.$refs.fileInput.getAcceptedFiles();
        if (files && files.length > 0) {
          this.file = files;
        } else {
          this.file = [];
         
        }
      });
    },
    async loadCustomers() {
      this.customers = (await Customer.getCustomers()).data
    },
    async getLayouts() {
      this.isLoading = true;

      const {
        data:
          {layouts, totalCount}
      } = await layoutAPI.getLayouts({
        params: {
          search: this.searchKey,
          page: this.page,
          limit: 20
        }
      });

      this.isLoading = false;

      this.layouts = this.layouts.concat(layouts);
      this.layoutsTotalCount = totalCount;
      this.page = this.page += 1;
    },

    customLabel({id, name}) {
      return `${id} - ${name}`;
    },

    searchCustomerLayouts(search) {
      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        this.page = 1;
        this.searchKey = search;
        this.layouts = [];

        this.getLayouts();
      }, 500);

    },
    processImport() {
      this.handleImportToLayout( {
        layout: this.selectedLayout[0],
        strategy: this.selectedStrategy,
        template_ids: this.selectedTemplates.templates.map(item => item.id),
        customer_id: this.selectedStrategy === IMPORT_AND_CREATE_STRATEGY ? this.selectedLayout[0].customer_id : this.selectedTemplates?.customer?.id,
      });
    },
    async handleImportToLayout(payload) {
      this.isImporting = true;
      this.layoutURL = null;
      try {
        let uploadedImage = [];
        if (this.importType === IMPORT_TYPE.DROPZONE) {
          const files = this.$refs.fileInput.getAcceptedFiles();
          if (files.length === 0) {
            toastr.error("No images to import");
            return;
          }
          const formData = new FormData();
          for (let i = 0; i < files.length; i++) {
            formData.append(`file_${i + 1}`, files[i]);
            formData.append(`filename_${i + 1}`, files[i].name);
          }
          formData.append('template_id', payload.template_ids[0]);
          formData.append('customer_id', payload.customer_id);
          const imageResponse = await axios.post('/banner/upload_bk_image', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
           uploadedImage = imageResponse.data;
        } else {
          uploadedImage = this.selectedItems.map((item) => {
            let correctPath = item.path;
            if (/share\?file=/.test(item.path)) { 
              const url = new URL(item.path);
              correctPath = url.searchParams.get('file');
            }
            return {
            ...item,
            path: correctPath,
          }}).filter((item) => item.type === 'image');
          
        }
        const createLayoutTemplatePayload = {
          backgroundThemeImages: uploadedImage.map(item => item.path),
          selectedLayout: payload.layout.id,
          strategy: payload.strategy
        };
        if (payload.strategy === IMPORT_INTO_EXISTS_TEMPLATE_STRATEGY) {
          createLayoutTemplatePayload.template_ids = payload.template_ids
        }
        const  createLayoutTemplateResponse = await axios.post(`/banner/${payload.customer_id}/group/create_layout_and_template_from_image`, createLayoutTemplatePayload, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        if (createLayoutTemplateResponse.data && createLayoutTemplateResponse.data.layout_id) {
          this.layoutURL = '/banner/' + createLayoutTemplateResponse.data.customer_id + '/group/' + createLayoutTemplateResponse.data.layout_id + '/show';
          toastr.success("Imported to layout successfully");
          this.loadingText = 'Redirecting to layout, please wait ...';
          window.location.href = this.layoutURL;
        } else {
          toastr.error("Failed to import to layout");
        }
      } catch (error) {
        this.isImporting = false;
        this.currentStep = 2;
        if (error.response && error.response.data && error.response.data.message) {
          toastr.error(error.response.data.message);
        } else {
          toastr.error("Failed to import to layout");
        }
      } 
    },
    handleNextStep() {
      if (this.currentStep <=2) {
        if (this.currentStep === 2 && this.importAvailable) {
          this.processImport();
        } else {
          this.currentStep++;
        }
      }
    },
    
    handlePrevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    onModalHide() {
      this.isOpen = false;
      this.loadingText = 'Importing, please wait...';
      this.currentStep = 1;
      this.importType = IMPORT_TYPE.DROPZONE;
      this.file = [];
      this.selectedItems = [];
      this.selectedLayout = [];
      this.imagesToImport = [];
      this.page = 1;
      this.searchKey = null;
      this.layouts = [];
    },
    handlePaste(event) {
      const items = event.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          const pastedFile = new File(
            [file],
            `pasted-image-${Date.now()}-${i}.png`,
            {
              type: file.type,
            }
          );
          this.$refs.fileInput.addFile(pastedFile);
        }
      }
     
    }
    
  },
}

</script>
<style scoped lang="scss">
  .import-image-browser {
    .selection-summary {
      display:none !important
    }
  }
</style>
